import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { signUp } from '../../../store/auth';
import EmailVerificationSentModal from './EmailVerificationSentModal';
import {
  CustomInput,
  CustomPasswordInput,
  CustomSelect,
  CustomCheckbox,
  CustomSubmitButton,
  CustomAlert,
  CustomModalHeader,
  CustomLinkButton,
  CustomFormStyles
} from './CustomFormComponents';

const SignUp = ({ show, handleClose, switchToLogin, onSignUpSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    usertype: '',
    agreeToNews: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

  const usertypes = [
    { value: 'Student', label: 'Student' },
    { value: 'Industry', label: 'Industry' },
    { value: 'Journalist', label: 'Journalist' },
    { value: 'Civil Societies', label: 'Civil Societies' },
    { value: 'Others', label: 'Others' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData.usertype) {
      newErrors.usertype = 'Please select a user type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    
    try {
      const signUpData = {
        name: formData.name.trim(),
        email: formData.email,
        password: formData.password,
        usertype: formData.usertype,
        agreeToNews: formData.agreeToNews
      };
      
      await signUp(signUpData);
      
      // Store email and show verification modal
      setVerificationEmail(formData.email);
      setShowVerificationModal(true);
      
      // Clear form data
      setFormData({
        name: '',
        email: '',
        password: '',
        usertype: '',
        agreeToNews: false
      });
      
      if (onSignUpSuccess) {
        onSignUpSuccess();
      }
      
    } catch (error) {
      console.error('Signup error:', error);
      
      if (error.message.includes('Email already registered') || 
          error.message.includes('User already registered') ||
          error.message.includes('Email already exists')) {
        setErrors({ general: 'An account with this email already exists. Please try logging in instead.' });
      } else if (error.message.includes('Invalid email format')) {
        setErrors({ email: 'Please enter a valid email address.' });
      } else if (error.message.includes('Password too weak')) {
        setErrors({ password: 'Password must be stronger. Include uppercase, lowercase, and numbers.' });
      } else if (error.message.includes('Name too short')) {
        setErrors({ name: 'Name must be at least 2 characters long.' });
      } else if (error.message.includes('Invalid user type')) {
        setErrors({ usertype: 'Please select a valid user type.' });
      } else {
        setErrors({ general: error.message || 'Sign up failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      usertype: '',
      agreeToNews: false
    });
    setErrors({});
    handleClose();
  };

  const handleVerificationModalClose = () => {
    setShowVerificationModal(false);
    setVerificationEmail('');
    handleModalClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleModalClose} centered size="md" backdrop="static" keyboard={false}>
        <CustomModalHeader 
          title="Sign Up" 
          onClose={handleModalClose} 
        />
        
        <Modal.Body className="px-4 pb-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <CustomAlert 
            type="danger" 
            message={errors.general} 
            show={!!errors.general} 
          />
          
          <form onSubmit={handleSubmit}>
            <CustomInput
              type="text"
              name="name"
              label="Name"
              required
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Name"
              error={errors.name}
              disabled={isLoading}
            />

            <CustomInput
              type="email"
              name="email"
              label="Email"
              required
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              error={errors.email}
              disabled={isLoading}
            />

            <CustomPasswordInput
              name="password"
              label="Password"
              required
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Password"
              error={errors.password}
              disabled={isLoading}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
            />

            <CustomSelect
              name="usertype"
              label="User Type"
              required
              value={formData.usertype}
              onChange={handleInputChange}
              error={errors.usertype}
              disabled={isLoading}
              options={usertypes}
              placeholder="Select user type"
            />

            <CustomCheckbox
              id="agreeToNews"
              name="agreeToNews"
              checked={formData.agreeToNews}
              onChange={handleInputChange}
              disabled={isLoading}
              label="I do not wish to receive news and updates from CDAP office by email"
            />

            <CustomSubmitButton
              isLoading={isLoading}
              loadingText="Creating Account..."
            >
              Sign Up
            </CustomSubmitButton>
          </form>

          <div className="text-center mt-3">
            <p className="mb-0 text-black">
              Already have an account?{' '}
              <CustomLinkButton
                onClick={switchToLogin}
                disabled={isLoading}
                className="inline align-baseline"
              >
                Sign in
              </CustomLinkButton>
            </p>
          </div>
        </Modal.Body>
      </Modal>

      <EmailVerificationSentModal 
        show={showVerificationModal}
        handleClose={handleVerificationModalClose}
        email={verificationEmail}
      />

      <CustomFormStyles />
    </>
  );
};

export default SignUp;